/*
Animaglass Global Phone Model Filter
------------------------------------

Controls the global device selection for the store.

Main features:
- Stores selected model in localStorage / cookie fallback
- Adds a global phone selector row below the header
- Adds a synchronized selector inside the mobile drawer
- Applies phone filtering to collection pages
- Switches product pages using the Animaglass SKU structure
- Preserves design, ecosystem, product type and selected case colour

SKU structure:
[design: 3][ecosystem: 1][phone: 3][type/colour: 1]

Storage key:
animaglass_phone_model
*/
(function () {
  'use strict';

  const STORAGE_KEY = 'animaglass_phone_model';
  const FILTER_PARAM = 'filter.p.m.custom.phone_model';
  const DEFAULT_MODEL = 'iPhone 17';

  const MODEL_CODES = Object.freeze({
    'iPhone 16': '16a',
    'iPhone 16 Pro Max': '16d',
    'iPhone 17': '17a',
    'iPhone 17 Pro': '17b'
  });

  const CODE_MODELS = Object.freeze(
    Object.fromEntries(
      Object.entries(MODEL_CODES).map(([model, code]) => [code, model])
    )
  );

  const normalizePath = (path) => (
    path.length > 1 ? path.replace(/\/+$/, '') : path
  );

  const targetPaths = new Set([
    '/collections/all',
    '/collections/cases',
    '/collections/backplates'
  ]);

  const isProductPage = () => (
    /\/products\/[^/]+\/?$/.test(window.location.pathname)
  );

  const shopRoot = () => (
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
  ) || '/';

  // ---------- Storage ----------

  const canUseLocalStorage = (() => {
    try {
      const testKey = '__ag__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  })();

  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
        name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') +
        '=([^;]*)'
      )
    );

    return match ? decodeURIComponent(match[1]) : null;
  };

  const setCookie = (name, value, days = 90) => {
    const expires = new Date(
      Date.now() + days * 864e5
    ).toUTCString();

    document.cookie =
      `${name}=${encodeURIComponent(value)}; ` +
      `expires=${expires}; path=/; SameSite=Lax`;
  };

  const safeGet = () => {
    if (canUseLocalStorage) {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch (error) {}
    }

    return getCookie(STORAGE_KEY);
  };

  const safeSet = (value) => {
    if (canUseLocalStorage) {
      try {
        localStorage.setItem(STORAGE_KEY, value);
        return;
      } catch (error) {}
    }

    setCookie(STORAGE_KEY, value);
  };

  const getCurrentModel = () => {
    const currentPath = normalizePath(window.location.pathname);

    if (targetPaths.has(currentPath)) {
      const urlValues = new URL(
        window.location.href
      ).searchParams.getAll(FILTER_PARAM);

      const urlModel = urlValues[urlValues.length - 1];

      if (urlModel && MODEL_CODES[urlModel]) {
        return urlModel;
      }
    }

    const storedModel = safeGet();

    return storedModel && MODEL_CODES[storedModel]
      ? storedModel
      : DEFAULT_MODEL;
  };

  // ---------- Selector styling ----------

  const injectStyles = () => {
    if (document.getElementById('ag-phone-selector-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'ag-phone-selector-styles';

    style.textContent = `
      .ag-phone-selector-wrap {
        align-items: center;
        color: inherit;
        display: flex;
        font: inherit;
        gap: 0.35rem;
        position: relative;
      }

      .ag-phone-selector-label {
        white-space: nowrap;
      }

      .ag-phone-selector-trigger {
        align-items: center;
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        display: inline-flex;
        font: inherit;
        gap: 0.55rem;
        justify-content: flex-end;
        line-height: inherit;
        margin: 0;
        max-width: 12rem;
        padding: 0.2rem 0;
      }

      .ag-phone-selector-trigger:focus-visible,
      .ag-phone-option:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      .ag-phone-selector-chevron {
        border-bottom: 1.4px solid currentColor;
        border-right: 1.4px solid currentColor;
        display: inline-block;
        height: 0.5rem;
        margin-top: -0.2rem;
        transform: rotate(45deg);
        transition: transform 150ms ease;
        width: 0.5rem;
      }

      .ag-phone-selector-trigger[aria-expanded="true"]
      .ag-phone-selector-chevron {
        margin-top: 0.2rem;
        transform: rotate(225deg);
      }

      .ag-phone-selector-popover {
        background: rgb(
          var(--color-background-rgb, 255 255 255)
        );
        border: 1px solid rgb(
          var(--color-foreground-rgb, 0 0 0) / 0.08
        );
        border-radius: 0.85rem;
        box-shadow: 0 0.6rem 1.7rem rgb(0 0 0 / 0.14);
        color: rgb(
          var(--color-foreground-rgb, 0 0 0)
        );
        min-width: 13.5rem;
        padding: 0.4rem;
        position: absolute;
        right: 0;
        top: calc(100% + 0.55rem);
        z-index: 1000;
      }

      .ag-phone-selector-popover[hidden] {
        display: none !important;
      }

      .ag-phone-option {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 0.5rem;
        color: inherit;
        cursor: pointer;
        display: flex;
        font: inherit;
        gap: 0.6rem;
        padding: 0.55rem 0.65rem;
        text-align: left;
        width: 100%;
      }

      .ag-phone-option:hover {
        background: rgb(
          var(--color-foreground-rgb, 0 0 0) / 0.05
        );
      }

      .ag-phone-option-check {
        align-items: center;
        border: 1px solid rgb(
          var(--color-foreground-rgb, 0 0 0) / 0.35
        );
        border-radius: 0.22rem;
        display: inline-flex;
        flex: 0 0 auto;
        height: 1rem;
        justify-content: center;
        width: 1rem;
      }

      .ag-phone-option[aria-selected="true"]
      .ag-phone-option-check {
        background: rgb(
          var(--color-foreground-rgb, 0 0 0)
        );
        border-color: rgb(
          var(--color-foreground-rgb, 0 0 0)
        );
      }

      .ag-phone-option[aria-selected="true"]
      .ag-phone-option-check::after {
        color: rgb(
          var(--color-background-rgb, 255 255 255)
        );
        content: '✓';
        font-size: 0.72rem;
        font-weight: 700;
        line-height: 1;
      }

      .ag-phone-selector-wrap[aria-busy="true"]::after {
        animation: ag-phone-spin 0.7s linear infinite;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        content: '';
        height: 0.75rem;
        margin-left: 0.15rem;
        width: 0.75rem;
      }

      .ag-phone-mobile-bar {
        align-items: center;
        background: rgb(
          var(--color-background-rgb, 255 255 255)
        );
        border-bottom: 1px solid rgb(
          var(--color-foreground-rgb, 0 0 0) / 0.12
        );
        color: rgb(
          var(--color-foreground-rgb, 0 0 0)
        );
        display: flex;
        justify-content: center;
        min-height: 2.75rem;
        padding: 0.55rem 1.5rem;
        width: 100%;
      }

      .ag-phone-mobile-bar .ag-phone-selector-wrap {
        justify-content: space-between;
        margin: 0 auto;
        max-width: 42rem;
        width: 100%;
      }

      .ag-phone-mobile-bar .ag-phone-selector-trigger {
        max-width: 12rem;
        width: 10.5rem;
      }

      .ag-phone-drawer-control {
        color: inherit;
        padding: 1rem 2.5rem 0.8rem;
        position: relative;
        z-index: 2;
      }

      .ag-phone-drawer-control
      .ag-phone-selector-wrap {
        justify-content: space-between;
        width: 100%;
      }

      .ag-phone-drawer-control
      .ag-phone-selector-trigger {
        max-width: 12rem;
        width: 10.5rem;
      }

      .ag-phone-drawer-control
      .ag-phone-selector-popover {
        left: auto;
        right: 0;
      }

      .ag-phone-notice {
        background: rgb(
          var(--color-background-rgb, 255 255 255)
        );
        border: 1px solid rgb(
          var(--color-foreground-rgb, 0 0 0) / 0.2
        );
        bottom: 1.25rem;
        color: rgb(
          var(--color-foreground-rgb, 0 0 0)
        );
        left: 50%;
        max-width: calc(100vw - 2rem);
        padding: 0.8rem 1rem;
        position: fixed;
        transform: translateX(-50%);
        z-index: 10000;
      }

      @keyframes ag-phone-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `;

    document.head.appendChild(style);
  };

  // ---------- Selector controls ----------

  const closeSelectorMenus = (except = null) => {
    document
      .querySelectorAll('.ag-phone-selector-wrap')
      .forEach((wrapper) => {
        if (wrapper === except) {
          return;
        }

        const trigger = wrapper.querySelector(
          '.ag-phone-selector-trigger'
        );

        const popover = wrapper.querySelector(
          '.ag-phone-selector-popover'
        );

        if (trigger) {
          trigger.setAttribute(
            'aria-expanded',
            'false'
          );
        }

        if (popover) {
          popover.hidden = true;
        }
      });
  };

  const syncControls = (model = getCurrentModel()) => {
    document
      .querySelectorAll('.ag-phone-selector-wrap')
      .forEach((wrapper) => {
        const value = wrapper.querySelector(
          '.ag-phone-selector-value'
        );

        if (value) {
          value.textContent = model;
        }

        wrapper
          .querySelectorAll('.ag-phone-option')
          .forEach((option) => {
            const selected =
              option.getAttribute('data-model') === model;

            option.setAttribute(
              'aria-selected',
              selected ? 'true' : 'false'
            );
          });
      });
  };

  const setControlsBusy = (busy) => {
    document
      .querySelectorAll(
        '.ag-phone-selector-trigger, .ag-phone-option'
      )
      .forEach((control) => {
        control.disabled = busy;
      });

    document
      .querySelectorAll('.ag-phone-selector-wrap')
      .forEach((wrapper) => {
        wrapper.setAttribute(
          'aria-busy',
          busy ? 'true' : 'false'
        );
      });
  };

  const showNotice = (message) => {
    const previousNotice = document.querySelector(
      '.ag-phone-notice'
    );

    if (previousNotice) {
      previousNotice.remove();
    }

    const notice = document.createElement('div');
    notice.className = 'ag-phone-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = message;

    document.body.appendChild(notice);

    window.setTimeout(() => {
      notice.remove();
    }, 4500);
  };

  const createSelector = (location) => {
    const wrapper = document.createElement('div');

    wrapper.className = 'ag-phone-selector-wrap';
    wrapper.setAttribute(
      'data-ag-phone-selector',
      location
    );
    wrapper.setAttribute('aria-busy', 'false');

    const id = `ag-phone-selector-${location}`;

    const label = document.createElement('label');
    label.className = 'ag-phone-selector-label';
    label.htmlFor = id;
    label.textContent = 'Phone model:';

    const trigger = document.createElement('button');
    trigger.id = id;
    trigger.type = 'button';
    trigger.className = 'ag-phone-selector-trigger';
    trigger.setAttribute(
      'aria-label',
      'Choose phone model'
    );
    trigger.setAttribute(
      'aria-haspopup',
      'listbox'
    );
    trigger.setAttribute(
      'aria-expanded',
      'false'
    );

    const value = document.createElement('span');
    value.className = 'ag-phone-selector-value';
    value.textContent = getCurrentModel();

    const chevron = document.createElement('span');
    chevron.className =
      'ag-phone-selector-chevron';
    chevron.setAttribute('aria-hidden', 'true');

    const popover = document.createElement('div');
    popover.id = `${id}-options`;
    popover.className =
      'ag-phone-selector-popover';
    popover.setAttribute('role', 'listbox');
    popover.setAttribute(
      'aria-label',
      'Phone models'
    );
    popover.hidden = true;

    trigger.setAttribute(
      'aria-controls',
      popover.id
    );

    Object.keys(MODEL_CODES).forEach((model) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'ag-phone-option';
      option.setAttribute('role', 'option');
      option.setAttribute(
        'data-model',
        model
      );
      option.setAttribute(
        'aria-selected',
        model === getCurrentModel()
          ? 'true'
          : 'false'
      );

      const check = document.createElement('span');
      check.className = 'ag-phone-option-check';
      check.setAttribute('aria-hidden', 'true');

      const optionText = document.createElement('span');
      optionText.textContent = model;

      option.append(check, optionText);

      option.addEventListener('click', (event) => {
        event.stopPropagation();
        closeSelectorMenus();
        selectPhoneModel(model);
      });

      popover.appendChild(option);
    });

    trigger.append(value, chevron);

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();

      const willOpen = popover.hidden;

      closeSelectorMenus(
        willOpen ? wrapper : null
      );

      popover.hidden = !willOpen;

      trigger.setAttribute(
        'aria-expanded',
        willOpen ? 'true' : 'false'
      );
    });

    popover.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        event.preventDefault();
        closeSelectorMenus();
        trigger.focus();
      }
    );

    wrapper.append(
      label,
      trigger,
      popover
    );

    return wrapper;
  };

  document.addEventListener('click', () => {
    closeSelectorMenus();
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        closeSelectorMenus();
      }
    }
  );

  // ---------- Inject selectors ----------

  let injectScheduled = false;

  const injectSelectors = () => {
    injectScheduled = false;
    injectStyles();

    const header = document.querySelector(
      'header-component'
    );

    if (
  isProductPage() &&
  header &&
  !document.querySelector(
    '[data-ag-phone-selector="mobile-bar"]'
  )
) {
      const row = document.createElement('div');

      row.className = 'ag-phone-mobile-bar';

      row.appendChild(
        createSelector('mobile-bar')
      );

      header.insertAdjacentElement(
        'afterend',
        row
      );
    }

    const drawerNavigation = document.querySelector(
      '.menu-drawer .menu-drawer__navigation'
    );

    if (
      drawerNavigation &&
      !document.querySelector(
        '[data-ag-phone-selector="drawer"]'
      )
    ) {
      const drawerControl =
        document.createElement('div');

      drawerControl.className =
        'ag-phone-drawer-control';

      drawerControl.appendChild(
        createSelector('drawer')
      );

      drawerNavigation.insertAdjacentElement(
        'beforebegin',
        drawerControl
      );
    }

    syncControls();
  };

  const scheduleSelectorInjection = () => {
    if (injectScheduled) {
      return;
    }

    injectScheduled = true;

    window.requestAnimationFrame(
      injectSelectors
    );
  };

  // ---------- Collection link handling ----------

  const withModelParam = (href, model) => {
    try {
      const url = new URL(
        href,
        window.location.origin
      );

      if (url.origin !== window.location.origin) {
        return null;
      }

      const path = normalizePath(url.pathname);

      if (!targetPaths.has(path)) {
        return null;
      }

      url.searchParams.delete(FILTER_PARAM);
      url.searchParams.set(
        FILTER_PARAM,
        model
      );

      return url.toString();
    } catch (error) {
      return null;
    }
  };

  const patchLinksOnce = () => {
    const model = getCurrentModel();

    document
      .querySelectorAll('a[href]')
      .forEach((link) => {
        const patched = withModelParam(
          link.getAttribute('href') || link.href,
          model
        );

        if (patched) {
          link.href = patched;
        }
      });
  };

  // ---------- Product switching ----------

  let currentProductPromise = null;

  const getProductJson = async (handleOrPath) => {
    let path;

    if (handleOrPath.startsWith('/')) {
      path = `${normalizePath(handleOrPath)}.js`;
    } else {
      path =
        `${shopRoot()}products/` +
        `${encodeURIComponent(handleOrPath)}.js`;
    }

    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Product request failed: ${response.status}`
      );
    }

    return response.json();
  };

  const getCurrentProduct = () => {
    if (!currentProductPromise) {
      currentProductPromise = getProductJson(
        window.location.pathname
      );
    }

    return currentProductPromise;
  };

  const getSelectedVariant = (product) => {
    const checkedVariant = document.querySelector(
      'input[data-variant-id]:checked'
    );

    const urlVariantId = new URL(
      window.location.href
    ).searchParams.get('variant');

    const formVariantId = Array.from(
      document.querySelectorAll('[name="id"]')
    )
      .map((element) => element.value)
      .find(Boolean);

    const selectedId =
      (
        checkedVariant &&
        checkedVariant.getAttribute('data-variant-id')
      ) ||
      urlVariantId ||
      formVariantId;

    return (
      product.variants.find(
        (variant) =>
          String(variant.id) === String(selectedId)
      ) ||
      product.variants[0] ||
      null
    );
  };

  const skuParts = (sku) => {
    const cleanSku = String(
      sku || ''
    ).trim();

    if (cleanSku.length < 8) {
      return null;
    }

    const phoneCode = cleanSku
      .slice(4, 7)
      .toLowerCase();

    if (!CODE_MODELS[phoneCode]) {
      return null;
    }

    return {
      sku: cleanSku,
      phoneCode,
      model: CODE_MODELS[phoneCode],
      prefix: cleanSku.slice(0, 4),
      suffix: cleanSku.slice(7)
    };
  };

  const buildTargetSku = (
    currentSku,
    model
  ) => {
    const parts = skuParts(currentSku);
    const phoneCode = MODEL_CODES[model];

    if (!parts || !phoneCode) {
      return null;
    }

    return (
      `${parts.prefix}` +
      `${phoneCode}` +
      `${parts.suffix}`
    );
  };

  const searchProductsBySku = async (sku) => {
    const params = new URLSearchParams();

    params.set('q', sku);
    params.set(
      'resources[type]',
      'product'
    );
    params.set(
      'resources[limit]',
      '10'
    );
    params.set(
      'resources[options][unavailable_products]',
      'show'
    );
    params.set(
      'resources[options][fields]',
      'variants.sku'
    );

    const response = await fetch(
      `${shopRoot()}search/suggest.json?${params.toString()}`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `SKU search failed: ${response.status}`
      );
    }

    const data = await response.json();

    return (
      (
        (
          (data || {}).resources || {}
        ).results || {}
      ).products || []
    );
  };

  const findTargetVariant = async (
    targetSku,
    allowColourFallback
  ) => {
    const exactSku = targetSku.toLowerCase();

    const targetStem = targetSku
      .slice(0, 7)
      .toLowerCase();

    let suggestions = await searchProductsBySku(
      targetSku
    );

    if (
      !suggestions.length &&
      allowColourFallback
    ) {
      suggestions = await searchProductsBySku(
        targetSku.slice(0, 7)
      );
    }

    for (const suggestion of suggestions) {
      if (!suggestion.handle) {
        continue;
      }

      const product = await getProductJson(
        suggestion.handle
      );

      let variant = product.variants.find(
        (candidate) =>
          String(candidate.sku || '')
            .trim()
            .toLowerCase() === exactSku
      );

      if (!variant && allowColourFallback) {
        variant =
          product.variants.find(
            (candidate) =>
              String(candidate.sku || '')
                .trim()
                .toLowerCase()
                .startsWith(targetStem) &&
              candidate.available
          ) ||
          product.variants.find(
            (candidate) =>
              String(candidate.sku || '')
                .trim()
                .toLowerCase()
                .startsWith(targetStem)
          );
      }

      if (variant) {
        return {
          product,
          variant
        };
      }
    }

    return null;
  };

  const productUrlForVariant = (
    product,
    variant
  ) => {
    const url = new URL(
      `${shopRoot()}products/` +
      encodeURIComponent(product.handle),
      window.location.origin
    );

    url.searchParams.set(
      'variant',
      variant.id
    );

    return url.toString();
  };

  const switchProductModel = async (model) => {
    const currentProduct =
      await getCurrentProduct();

    const currentVariant =
      getSelectedVariant(currentProduct);

    const parts =
      currentVariant &&
      skuParts(currentVariant.sku);

    if (!currentVariant || !parts) {
      throw new Error(
        'The current product does not have a recognized Animaglass SKU.'
      );
    }

    const targetSku = buildTargetSku(
      currentVariant.sku,
      model
    );

    if (!targetSku) {
      throw new Error(
        'The selected phone model could not be encoded.'
      );
    }

    if (
      targetSku.toLowerCase() ===
      String(currentVariant.sku)
        .trim()
        .toLowerCase()
    ) {
      return {
        samePage: true,
        currentModel: model
      };
    }

    const allowColourFallback =
      parts.suffix.toLowerCase() !== '0';

    const match = await findTargetVariant(
      targetSku,
      allowColourFallback
    );

    if (!match) {
      return {
        unavailable: true,
        currentModel: parts.model
      };
    }

    window.location.assign(
      productUrlForVariant(
        match.product,
        match.variant
      )
    );

    return {
      navigating: true
    };
  };

  const syncModelFromProduct = async () => {
    if (!isProductPage()) {
      return;
    }

    try {
      const product =
        await getCurrentProduct();

      const variant =
        getSelectedVariant(product);

      const parts =
        variant &&
        skuParts(variant.sku);

      if (!parts) {
        return;
      }

      safeSet(parts.model);
      syncControls(parts.model);
      patchLinksOnce();
    } catch (error) {
      // Product browsing remains functional.
    }
  };

  // ---------- Selector navigation ----------

  const selectPhoneModel = async (model) => {
    if (!MODEL_CODES[model]) {
      return;
    }

    const previousModel =
      getCurrentModel();

    safeSet(model);
    syncControls(model);
    patchLinksOnce();

    const path = normalizePath(
      window.location.pathname
    );

    if (targetPaths.has(path)) {
      const url = new URL(
        window.location.href
      );

      url.searchParams.delete(
        FILTER_PARAM
      );

      url.searchParams.set(
        FILTER_PARAM,
        model
      );

      window.location.assign(
        url.toString()
      );

      return;
    }

    if (!isProductPage()) {
      return;
    }

    setControlsBusy(true);

    try {
      const result =
        await switchProductModel(model);

      if (
        result &&
        result.unavailable
      ) {
        const restoredModel =
          result.currentModel ||
          previousModel;

        safeSet(restoredModel);
        syncControls(restoredModel);
        patchLinksOnce();

        showNotice(
          `This design is not available for ${model}.`
        );

        setControlsBusy(false);
      } else if (
        result &&
        result.samePage
      ) {
        setControlsBusy(false);
      }
    } catch (error) {
      safeSet(previousModel);
      syncControls(previousModel);
      patchLinksOnce();

      showNotice(
        'Unable to change the phone model. Please try again.'
      );

      setControlsBusy(false);
    }
  };

  // ---------- Restore collection filter ----------

  const restoreStoredModel = () => {
    const model = getCurrentModel();

    safeSet(model);

    const url = new URL(
      window.location.href
    );

    url.searchParams.delete(
      FILTER_PARAM
    );

    url.searchParams.set(
      FILTER_PARAM,
      model
    );

    window.location.replace(
      url.toString()
    );
  };

  const ensurePhoneFilterPresent = () => {
    const path = normalizePath(
      window.location.pathname
    );

    if (!targetPaths.has(path)) {
      return;
    }

    const url = new URL(
      window.location.href
    );

    if (
      !url.searchParams.has(FILTER_PARAM)
    ) {
      restoreStoredModel();
    }
  };

  // ---------- Site-wide initialization ----------

  injectStyles();
  scheduleSelectorInjection();
  patchLinksOnce();

  const headerObserver =
    new MutationObserver(() => {
      scheduleSelectorInjection();
    });

  headerObserver.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target &&
        event.target.closest
          ? event.target.closest('a[href]')
          : null;

      if (!link) {
        return;
      }

      const model = getCurrentModel();

      const patched = withModelParam(
        link.getAttribute('href') ||
        link.href,
        model
      );

      if (!patched || link.href === patched) {
        return;
      }

      link.href = patched;
    },
    true
  );

  syncModelFromProduct();

  // ---------- Collection-only behaviour ----------

  const currentPath = normalizePath(
    window.location.pathname
  );

  if (!targetPaths.has(currentPath)) {
    return;
  }

  const collectionUrl = new URL(
    window.location.href
  );

  const collectionParams =
    collectionUrl.searchParams;

  const filterValues =
    collectionParams.getAll(FILTER_PARAM);

  if (filterValues.length > 0) {
    const selectedModel =
      filterValues[filterValues.length - 1];

    if (MODEL_CODES[selectedModel]) {
      safeSet(selectedModel);
      syncControls(selectedModel);
    }

    if (filterValues.length > 1) {
      collectionParams.delete(
        FILTER_PARAM
      );

      collectionParams.set(
        FILTER_PARAM,
        selectedModel
      );

      window.location.replace(
        collectionUrl.toString()
      );

      return;
    }
  } else {
    const model = getCurrentModel();

    collectionParams.set(
      FILTER_PARAM,
      model
    );

    safeSet(model);

    window.location.replace(
      collectionUrl.toString()
    );

    return;
  }

  // ---------- Native collection facet ----------

  const handleFacetChange = (event) => {
    const element = event.target;

    if (
      !element ||
      element.tagName !== 'INPUT' ||
      element.type !== 'checkbox' ||
      element.name !== FILTER_PARAM
    ) {
      return;
    }

    const chosenModel = element.value;

    if (!chosenModel) {
      return;
    }

    if (!element.checked) {
      element.checked = true;
      return;
    }

    document
      .querySelectorAll(
        `input[type="checkbox"][name="${CSS.escape(FILTER_PARAM)}"]`
      )
      .forEach((checkbox) => {
        if (checkbox !== element) {
          checkbox.checked = false;
        }
      });

    safeSet(chosenModel);
    syncControls(chosenModel);
    patchLinksOnce();
  };

  document.addEventListener(
    'change',
    handleFacetChange,
    true
  );

  // ---------- Clear/reset behaviour ----------

  const looksLikeClearClick = (node) => {
    const text = (
      (node && node.textContent) || ''
    ).trim().toLowerCase();

    const aria = (
      (
        node &&
        node.getAttribute &&
        node.getAttribute('aria-label')
      ) || ''
    ).toLowerCase();

    const href = (
      (
        node &&
        node.getAttribute &&
        node.getAttribute('href')
      ) || ''
    );

    return (
      text.includes('clear') ||
      text.includes('reset') ||
      aria.includes('clear') ||
      aria.includes('reset') ||
      href.includes('clear') ||
      href.includes('reset')
    );
  };

  document.addEventListener(
    'click',
    (event) => {
      const element =
        event.target &&
        event.target.closest
          ? event.target.closest('a,button')
          : null;

      if (
        !element ||
        !looksLikeClearClick(element)
      ) {
        return;
      }

      const model = getCurrentModel();

      const url = new URL(
        window.location.href
      );

      const keysToDelete = [];

      url.searchParams.forEach(
        (value, key) => {
          if (
            key.startsWith('filter.') &&
            key !== FILTER_PARAM
          ) {
            keysToDelete.push(key);
          }
        }
      );

      keysToDelete.forEach((key) => {
        url.searchParams.delete(key);
      });

      url.searchParams.delete(
        FILTER_PARAM
      );

      url.searchParams.set(
        FILTER_PARAM,
        model
      );

      event.preventDefault();
      event.stopPropagation();

      window.location.assign(
        url.toString()
      );
    },
    true
  );

  // ---------- AJAX URL monitoring ----------

  const originalPushState =
    history.pushState;

  history.pushState = function () {
    originalPushState.apply(
      this,
      arguments
    );

    window.setTimeout(
      ensurePhoneFilterPresent,
      0
    );
  };

  const originalReplaceState =
    history.replaceState;

  history.replaceState = function () {
    originalReplaceState.apply(
      this,
      arguments
    );

    window.setTimeout(
      ensurePhoneFilterPresent,
      0
    );
  };

  window.addEventListener(
    'popstate',
    () => {
      window.setTimeout(
        ensurePhoneFilterPresent,
        0
      );
    }
  );
})();